/**
 * Muidrawer.js - Layout aligned with D:\ForBuild\admin4.0\src\Shell\Muidrawer.js:
 * Box eMed_muiDrawer, AppBar with Toolbar (profile, Avatar menu), Drawer, RouterComponent.
 * Uses CacheProviderService (sessionStorage: getCachevalue / clearCachevalue).
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import IconButton from "@mui/material/IconButton";
import CssBaseline from "@mui/material/CssBaseline";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useTheme } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import GridViewIcon from "@mui/icons-material/GridView";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import WarningIcon from "@mui/icons-material/Warning";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import { getCachevalue, clearCachevalue } from "../Utility/Services/CacheProviderService";
import { getSideMenus } from "../Utility/SideMenuList";
import RouterComponent from "../Utility/RouterComponent";
import { Colors } from "../Styles/Colors";
import { APP_Version_Label, APP_Support_Label } from "../Utility/Services/RestAPIService";
import ImagePaths from "../Utility/ImagePaths";
import "./NavbarApp.css";

const drawerWidth = "20vw";

const contentInTooltip = (str, len) =>
  str && str.length > len ? `${str.slice(0, len)}...` : str || "";
const t = (key) => key;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth})`,
      transition: theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
  })
);

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    ...(open && {
      ...openedMixin(theme),
      "& .MuiDrawer-paper": openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      "& .MuiDrawer-paper": closedMixin(theme),
    }),
  })
);

export default function Muidrawer() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [loginName, setLoginName] = useState("");
  const [idleAlertPopup, setIdleAlertPopup] = useState(false);
  const idleTimerRef = useRef(null);
  const idleAlertTimerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isLoggedIn = getCachevalue("isLoggedIn");
  const menuList = isLoggedIn ? getSideMenus(getCachevalue) : [];
  const myUser = JSON.parse(getCachevalue("loggedInUserInfo") || "{}");
  const DisableSwitchModule = false;
  const isDashboard = location.pathname === "/HistopathologyHome";

  useEffect(() => {
    if (myUser?.username) setLoginName(myUser.username);
  }, [myUser?.username]);

  const handleDrawerClose = () => setDrawerOpen(false);
  const handleDrawerOpen = () => setDrawerOpen(true);
  const handleCloseUserMenu = () => setAnchorElUser(null);

  const clearAccessCache = () => {};
  const cacheKeysToClear = [
    "isCanteenOverall", "IsPORequestEdit", "Laboratory_id", "subInbound", "subOutbound",
    "branchInbound", "branchOutbound", "subTotal", "branchTotal", "LabStatusDateFilter",
    "LabCancelledDateFilter", "LabStatusDateCustumFilter", "LabTstReportDateFilter",
    "LabForTechDateFilter", "SelectedOPDate", "SelectedDate", "OP_CreateAppointment_PatientId",
    "BillType", "FromOtherScreen", "Status", "FormDate", "ToDate",
  ];

  const handleSwitchModule = () => {
    handleCloseUserMenu();
    setLoginName("");
    setAnchorElUser(null);
    clearCachevalue("AccessToken");
    clearCachevalue("loggedInUserInfo");
    clearCachevalue("clinicId");
    clearCachevalue("is_investigation_module");
    clearCachevalue("investigation_name");
    cacheKeysToClear.forEach((k) => clearCachevalue(k));
    clearAccessCache();
    navigate("/HistopathologyHome");
  };

  const logout = useCallback(() => {
    clearCachevalue("AccessToken");
    clearCachevalue("loggedInUserInfo");
    clearCachevalue("isLoggedIn");
    clearCachevalue("LoginAuthToken");
    clearCachevalue("LogedUserName");
    clearCachevalue("loggedUserId");
    clearCachevalue("BranchId");
    clearCachevalue("BranchName");
    clearCachevalue("auto_log_out_time");
    clearCachevalue("Scrren_access");
    clearCachevalue("clinicId");
    cacheKeysToClear.forEach((k) => clearCachevalue(k));
    clearAccessCache();
    navigate("/");
  }, [navigate]);

  const clearTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (idleAlertTimerRef.current) clearTimeout(idleAlertTimerRef.current);
    idleTimerRef.current = null;
    idleAlertTimerRef.current = null;
  }, []);

  const autoLogOutOnIdle = useCallback(() => {
    if (location.pathname === "/") {
      clearTimer();
    } else {
      clearTimer();
      logout();
    }
  }, [location.pathname, clearTimer, logout]);

  const startTimer = useCallback(() => {
    setIdleAlertPopup(false);
    const raw = getCachevalue("auto_log_out_time");
    const customLogoutTime = raw ? Number(raw) : 15;
    const logoutMs = customLogoutTime * 60 * 1000;
    const alertMs = (customLogoutTime - 3) * 60 * 1000;
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (idleAlertTimerRef.current) clearTimeout(idleAlertTimerRef.current);
    idleTimerRef.current = setTimeout(autoLogOutOnIdle, logoutMs);
    idleAlertTimerRef.current = setTimeout(() => setIdleAlertPopup(true), Math.max(0, alertMs));
  }, [autoLogOutOnIdle]);

  const resetIdleTimer = useCallback(() => {
    clearTimer();
    startTimer();
  }, [clearTimer, startTimer]);

  useEffect(() => {
    if (!isLoggedIn) return;
    startTimer();
    const events = ["mousemove", "mousedown", "touchstart", "touchmove", "click", "keydown"];
    const onActivity = () => resetIdleTimer();
    events.forEach((ev) => window.addEventListener(ev, onActivity));
    window.addEventListener("scroll", onActivity, true);
    return () => {
      clearTimer();
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      window.removeEventListener("scroll", onActivity, true);
    };
  }, [isLoggedIn, startTimer, resetIdleTimer, clearTimer]);

  const profileMenuDisabled =
    !(location.pathname === "/NoAccess") &&
    (getCachevalue("isForCancelBill") === "true" ||
      getCachevalue("IsForCheckedOut") === "true" ||
      getCachevalue("isInRadiologyBilling") === "true" ||
      getCachevalue("DisableAllActions") === "true");
  const avatarDisabled = profileMenuDisabled || location.pathname === "/video-call";

  const isActive = (item) => {
    const path = `/${item.menuName.replace(/\s/g, "")}`;
    const current = location.pathname.split("/")[1] || "";
    const menuSlug = item.menuName.replace(/\s/g, "");
    return current === menuSlug || (item.menuName === "MainHome" && location.pathname === "/Results");
  };

  if (!isLoggedIn) {
    return <RouterComponent />;
  }

  return (
    <div>
      <Box className="eMed_muiDrawer">
        <CssBaseline />
        <AppBar sx={{ minHeight: "4.1vw" }} position="fixed" open={drawerOpen}>
          <Toolbar>
            <div className="fieldHeaderfordoctor">
              {isDashboard ? (
                <div className="eMed_profile">
                  <div className="eMed_user">
                    <label className="eMed_loginuser">{getCachevalue("LogedUserName")}</label>
                  </div>
                </div>
              ) : (
                <div className="eMed_profile">
                  <div className="eMed_user">
                    <label className="eMed_loginuser">{loginName || myUser?.username || getCachevalue("LogedUserName")}</label>
                  </div>
                  <div className="eMed_servicetype">
                    <label className="eMed_LoginType">Histopathology</label>
                  </div>
                </div>
              )}
              <div className="eMed_Avatar">
                <Box>
                  <Tooltip title="Profile" arrow>
                    <IconButton
                      type="button"
                      disabled={avatarDisabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        setAnchorElUser(e.currentTarget);
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: Colors.IconCircleBgColor,
                          boxShadow: 3,
                          color: Colors.ThemeColor,
                        }}
                        id="eMed_Icons_Avatar"
                      />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    id="eMed_profileMenu"
                    anchorEl={anchorElUser}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    keepMounted
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                    sx={{ marginRight: "5vw" }}
                    TransitionProps={{ timeout: 0 }}
                    disableAutoFocus
                    disableRestoreFocus
                    MenuListProps={{ onClick: (e) => e.stopPropagation() }}
                  >
                    <div className="eMed_AvatarMenu">
                      {DisableSwitchModule ? null : !location.pathname.includes("/HistopathologyHome") ? (
                        <MenuItem
                          onClick={handleSwitchModule}
                        >
                          <GridViewIcon sx={{ color: Colors.ThemeColor, marginRight: "0.5vw" }} />
                          {t("SwitchModule")}
                        </MenuItem>
                      ) : null}
                      <MenuItem
                        onClick={() => {
                          handleCloseUserMenu();
                          logout();
                        }}
                      >
                        <ExitToAppIcon sx={{ color: Colors.ThemeColor, marginRight: "0.5vw" }} />
                        {t("Logout")} / <span>{contentInTooltip(getCachevalue("LogedUserName"), 15)}</span>
                      </MenuItem>
                    </div>
                  </Menu>
                </Box>
              </div>
            </div>
          </Toolbar>
        </AppBar>

        {location.pathname !== "/video-call" && (
          <Drawer className="eMed_Drawer" variant="permanent" open={drawerOpen}>
            <DrawerHeader className="eMed_drawerlogo" sx={{ backgroundColor: Colors.pageTopBackground }}>
              <IconButton onClick={drawerOpen ? handleDrawerClose : handleDrawerOpen}>
                {theme.direction === "rtl" ? (
                  <ChevronRightIcon sx={{ color: Colors.ListIcons }} />
                ) : (
                  <ChevronLeftIcon sx={{ color: Colors.ListIcons }} />
                )}
              </IconButton>
            </DrawerHeader>
            <List id="eMed_side_list">
              {menuList.map((item, index) => {
                const active = isActive(item);
                const displayLabel = item.label.split(" ");
                return (
                  <ListItem
                    key={item.menuName}
                    className="eMed_menuItems"
                    sx={{
                      maxWidth: "3.3vw",
                      marginBottom: "1.5vh",
                      alignItems: "center",
                      display: "flex",
                      justifyContent: "center",
                      padding: 0,
                    }}
                    onClick={() => navigate(`/${item.menuName.replace(/\s/g, "")}`)}
                  >
                    <Tooltip placement="right" title={item.label} disableInteractive arrow>
                      <ListItemIcon
                        className="eMed_menuIcon"
                        sx={{ display: "flex", flexDirection: "column", padding: 0, alignItems: "center" }}
                      >
                        <Avatar
                          src={active ? item.OnIcon?.default : item.OffIcon?.default}
                          sx={
                            active
                              ? { width: "3vw", height: "3vw" }
                              : { width: "2.5vw", height: "2.5vw" }
                          }
                          variant="square"
                        />
                        {displayLabel.map((word, i) => (
                          <Typography
                            key={i}
                            component="p"
                            variant="caption"
                            sx={{
                              maxWidth: "3.3vw",
                              fontSize: "0.6dvw",
                              color: active ? Colors.themeDark : Colors.disableLabelColor,
                              whiteSpace: "pre-line",
                              alignContent: "center",
                              justifyContent: "center",
                            }}
                          >
                            {word}
                          </Typography>
                        ))}
                      </ListItemIcon>
                    </Tooltip>
                  </ListItem>
                );
              })}
            </List>
          </Drawer>
        )}

        <Box component="main" sx={{ flexGrow: 1, minWidth: "10vw", pl: "3vw", pr: "1vw" }}>
          <RouterComponent />
        </Box>
      </Box>
      <Box sx={{ height: "1vh", width: "100%", zIndex: 1200, position: "fixed", bottom: "5vh", borderTop: "1px solid lightgrey", backgroundColor: Colors.BgColor }} />
      <Box sx={{ height: "5vh", width: "100%", zIndex: 1200, position: "fixed", bottom: 0, backgroundColor: Colors.ThemeColor, alignItems: "center", justifyContent: "center", display: "flex", flexDirection: "row" }}>
        <Avatar src={ImagePaths.Logo?.default} sx={{ width: "1vw", height: "1vw", marginRight: "0.25vw" }} />
        <Typography color={Colors.white} fontSize="1dvw">www.emedhub.in</Typography>
        <Typography sx={{ paddingLeft: 2, paddingRight: 5 }} color={Colors.white} fontSize="1dvw">{APP_Version_Label}</Typography>
        <Typography color={Colors.white} fontSize="1dvw">{APP_Support_Label}</Typography>
      </Box>
      {idleAlertPopup ? (
        <Box className="IdleNotiContainer">
          <Paper>
            <div className="IdleNotiWrapper">
              <div className="NotiHeaderContainer">
                <div className="NotiIcon">
                  <WarningIcon sx={{ height: "1.5vw", width: "1.5vw", color: "white" }} />
                </div>
                <div className="NotiText" style={{ fontWeight: 600 }}>System Logout Alert!</div>
              </div>
              <div className="NotiPara" style={{ paddingLeft: "10vw" }}>
                Your session will expire in few minutes due to inactivity. Please continue your activity to stay logged in.
              </div>
            </div>
          </Paper>
        </Box>
      ) : null}
    </div>
  );
}
